import { useEffect, useMemo, useState, useRef } from 'react';
import {
  GridRowModes,
  useGridApiRef,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid-premium';
import { Box, Button, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import { sxConfig } from './ProductionPlannerTable.style';
import { dataGridPremiumColumns, generateAutocompleteItems } from '../../../../utils/helper';
import {
  DataGridIdentifiers,
  InputUI,
  DatagridPremiumUi,
  DatePickerUI,
  AutoCompleteSelectUI,
  ConfirmDialogUI,
  SelectUI,
} from '../../../Interface';
import useConfig from '../../../../context/ConfigContext';
import useUsers from '../../../../context/UsersContext';
import useParam from '../../../../context/ParamContext';
import productionPlannerTableService from '../../../../service/client/varadiMetal/productionPlannerTable.service';
import masterDataService from '../../../../service/masterData.service';
import DrillArrow from '../../../Interface/DrillArrow';
import SourceListDialog from '../../../SourceList/SourceListDialog';
import { TechnologistStatuses } from './TechnologistStatuses';
import ProductionDialog from '../../../Production/NewProduction/ProductionDialog';
import { useLoader } from '../../../../provider/LoaderProvider';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';

const ProductionPlannerTable = () => {
  const apiRef = useGridApiRef();
  const { showLoader, hideLoader } = useLoader();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [updData, setUpdData] = useState({});
  const [selectedItemCode, setSelectedItemCode] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [lockedRowIds, setLockedRowIds] = useState([]); // rows locked during async updates

  // Debounce helpers
  const debounceTimersRef = useRef({});
  const scheduleDebounced = (key, fn, delay = 600) => {
    const timers = debounceTimersRef.current;
    if (timers[key]) clearTimeout(timers[key]);
    timers[key] = setTimeout(() => {
      delete timers[key];
      fn();
    }, delay);
  };

  // helper to lock / unlock rows
  const lockRow = (rowIds) => {
    if (rowIds == null || !rowIds.length) return;

    setLockedRowIds((prev) => [...new Set([...prev, ...rowIds])]);
  };

  const unlockRow = (rowIds) => {
    if (rowIds == null || !rowIds.length) return;
    setLockedRowIds((prev) => prev.filter((id) => !rowIds.includes(id)));
  };

  const getList = () => {
    const gridState = {
      filter: { filterModel: { items: [], logicOperator: 'and' } },
      sorting: { sortModel: [] },
      pagination: {},
    };
    return productionPlannerTableService.getOrders(gridState, null).then((res) => {
      setReports(res);
      return res;
    });
  };

  const getItemsToForm = () => {
    return masterDataService.itemsFromTableToForm().then((data) => {
      setAutocompleteData((prev) => ({ ...prev, items: data }));
      setAutoCompleteOptions((prev) => ({
        ...prev,
        items: generateAutocompleteItems(data, ['itemCode', 'itemName'], 'itemCode'),
      }));
      return data;
    });
  };

  const getData = () => {
    showLoader();
    return Promise.all([getList(), getItemsToForm()]).finally(() => {
      hideLoader();
    });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
    getData();
  }, []);

  // Clear any pending debounced tasks on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          users: resp[0],
        };
        setAutocompleteData(aData);

        const technologists = aData.users.filter((user) => user.technologist);

        const acOptions = {
          technologists: generateAutocompleteItems(technologists, 'nickName', 'userId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'dueDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  const handleFieldChange = async (fieldName, newValue, row, allLine = false) => {
    const docId = row?.doc?.docId;

    const findedLines = allLine ? reports.rows.filter((r) => r.doc?.docId === docId) : [row];
    const rowIds = findedLines.map((r) => r.id);

    try {
      lockRow(rowIds);

      const value = newValue === '' ? null : newValue;
      const requestBody = {
        ...row,
        allLine,
        [fieldName]: value,
      };

      await productionPlannerTableService.updateOrderLine(requestBody);

      const updatedLines = findedLines.map((r) => ({ ...r, [fieldName]: value }));

      setReports((prev) => {
        const map = new Map(prev.rows.map((r) => [r.id, r]));
        updatedLines.forEach((r) => {
          map.set(r.id, { ...map.get(r.id), ...r });
        });
        return {
          ...prev,
          rows: [...map.values()],
        };
      });
    } catch (error) {
      console.error('Error updating field:', error);
      getList();
    } finally {
      unlockRow(rowIds);
    }
  };

  const handleDocFieldChange = async (fieldNameOrUpdates, newValue, row) => {
    const docId = row?.doc?.docId;
    const findedLines = reports.rows.filter((r) => r.doc?.docId === docId);
    const rowIds = findedLines.map((r) => r.id);
    const fieldUpdates =
      typeof fieldNameOrUpdates === 'string'
        ? { [fieldNameOrUpdates]: newValue === '' ? null : newValue }
        : Object.fromEntries(
            Object.entries(fieldNameOrUpdates ?? {}).map(([field, value]) => [field, value === '' ? null : value])
          );

    try {
      lockRow(rowIds);

      const requestBody = {
        ...row.doc,
        ...fieldUpdates,
      };

      await productionPlannerTableService.updateOrderHead(requestBody);

      const updatedLines = findedLines.map((r) => ({
        ...r,
        doc: {
          ...(r.doc || {}),
          ...fieldUpdates,
        },
      }));

      setReports((prev) => {
        const map = new Map(prev.rows.map((r) => [r.id, r]));
        updatedLines.forEach((r) => {
          map.set(r.id, { ...map.get(r.id), ...r });
        });
        return {
          ...prev,
          rows: [...map.values()],
        };
      });
    } catch (error) {
      console.error('Error updating field:', error);
      getList();
    } finally {
      unlockRow(rowIds);
    }
  };

  const columns = useMemo(() => {
    const roleId = user.roleId;
    const ppCols = JSON.parse(params?.VM_PRODPLANNER_COLS ?? '{}');

    const allColumns = [
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.dueDate ? new Date(row.dueDate) : null;
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY.MM.DD');
        },
      },
      {
        field: 'doc_joker_date1',
        headerName: 'Logisztikai határidő',
        type: 'date',
        sortable: false,
        valueGetter: (_data, row) => {
          return row.doc?.joker_date1 ? new Date(row.doc?.joker_date1) : null;
        },
        renderCell: (data) => {
          return data.row.doc?.joker_date1 ? dayjs(data.row.doc?.joker_date1).format('YYYY.MM.DD') : '';
        },
      },

      {
        field: 'doc_docNum',
        headerName: 'Rendelés szám',
        type: 'string',
        valueGetter: (_val, row) => row.doc?.docNum ?? '',
        renderCell: (data) => {
          return (
            <div>
              {data.row.doc?.docNum}
              <DrillArrow
                onClick={(e) => {
                  productionDialogOpen(data.row.doc?.docId, data.row.lineNum);
                }}
              />
            </div>
          );
        },
      },

      {
        field: 'doc_companyCode',
        headerName: 'Üp. kód',
        type: 'string',
        sortable: false,
        valueGetter: (_val, row) => row.doc?.companyCode ?? '',
      },
      {
        field: 'doc_companyName',
        headerName: 'Üp. név',
        type: 'string',
        sortable: false,
        valueGetter: (_val, row) => row.doc?.companyName ?? '',
      },

      {
        field: 'itemReference',
        headerName: 'Hivatkozás',
        type: 'string',
        valueGetter: (_val, row) => {
          const docReference = row.doc?.reference;
          const itemReference = row.itemReference;

          return docReference && itemReference
            ? `${docReference} - ${itemReference}`
            : docReference || itemReference || '';
        },
      },

      {
        field: 'itemCode',
        headerName: 'Cikkszám',
        type: 'string',

        renderCell: (params) => {
          return (
            <div
              className={
                params.row.missingtree === 1 || params.row.missingOperation === 1
                  ? 'datagrid-line-missingTree'
                  : params.row.missingSourceList
                    ? 'datagrid-line-missingSourceList'
                    : params.row.isMaterial
                      ? 'datagrid-line-isMaterial'
                      : ''
              }
            >
              {params.row.itemCode}

              <DrillArrow
                onClick={(e) => {
                  sourceListDialogOpen(params.row.itemCode);
                }}
              />
            </div>
          );
        },
      },

      {
        field: 'itemName',
        headerName: 'Cikknév',
        type: 'string',
      },

      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },

      {
        field: 'joker_char1',
        headerName: 'Felületkezelés',
        type: 'string',
      },

      {
        field: 'qntStatus',
        headerName: 'Tétel állapot',
        type: 'string',
      },

      {
        field: 'joker_char3',
        headerName: 'Tétel hivatkozás',
        type: 'string',
      },

      {
        field: 'joker_date1',
        headerName: 'Várható leérkezés GL',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.joker_date1 ? new Date(row.joker_date1) : null;
        },
        renderCell: (data) => {
          return data.row.joker_date1 ? dayjs(data.row.joker_date1).format('YYYY.MM.DD') : '';
        },
        renderEditCell: (data) => {
          return (
            <DatePickerUI
              name="joker_date1"
              type="date"
              variant="standard"
              fullWidth
              value={data.row.joker_date1 ? dayjs(data.row.joker_date1) : null}
              onChange={async (e) => {
                setShowConfirmDialog(true);
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                setUpdData({ field: data.field, row: data.row, value: newValue });
                //handleFieldChange(data.field, newValue, data.row);
                await apiRef.current.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
            />
          );
        },
      },

      {
        field: 'joker_date2',
        headerName: 'Beérkezés dátuma',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.joker_date2 ? new Date(row.joker_date2) : null;
        },
        renderCell: (data) => {
          return data.row.joker_date2 ? dayjs(data.row.joker_date2).format('YYYY.MM.DD') : '';
        },
        renderEditCell: (data) => {
          return (
            <DatePickerUI
              name="joker_date2"
              type="date"
              variant="standard"
              fullWidth
              value={data.row.joker_date2 ? dayjs(data.row.joker_date2) : null}
              onChange={async (e) => {
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                await handleFieldChange(data.field, newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
            />
          );
        },
      },
      {
        field: 'joker_date3',
        headerName: 'Alkatrészgyártás befejezés!',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.joker_date3 ? new Date(row.joker_date3) : null;
        },
        renderCell: (data) => {
          return data.row.joker_date3 ? dayjs(data.row.joker_date3).format('YYYY.MM.DD') : '';
        },
        renderEditCell: (data) => {
          return (
            <DatePickerUI
              name="joker_date3"
              type="date"
              variant="standard"
              fullWidth
              value={dayjs(data.row.joker_date3)}
              onChange={(e) => {
                setShowConfirmDialog(true);
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                setUpdData({ field: data.field, row: data.row, value: newValue });
                //handleFieldChange(data.field, newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
            />
          );
        },
      },

      {
        field: 'joker_char4',
        headerName: 'Gyártási megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_char4"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.joker_char4}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                // Update UI immediately
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                // Debounce server update
                scheduleDebounced(
                  `joker_char4-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },

      {
        field: 'joker_char5',
        headerName: 'Beszállító hat. idő',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_char5"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.joker_char5}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                scheduleDebounced(
                  `joker_char5-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },

      {
        field: 'joker_char6',
        headerName: 'Beszállító hat. idő 2',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_char6"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.joker_char6}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                scheduleDebounced(
                  `joker_char6-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },
      {
        field: 'doc_joker_int2',
        headerName: 'Programozó',
        type: 'singleSelect',
        sortable: false,
        editable: true,
        valueGetter: (_data, row) => {
          return row.doc?.joker_int2;
        },
        valueOptions: autoCompleteOptions.technologists ?? [],
        renderEditCell: (data) => {
          const findedopts =
            autoCompleteOptions.technologists?.find((opt) => opt.value === data.row.doc?.joker_int2) ?? null;

          return (
            <AutoCompleteSelectUI
              name="programmer"
              variant="standard"
              onChange={(_e, newVal) => {
                const newValue = newVal?.value ?? null;
                const docUpdates = { joker_int2: newValue, joker_int1: newValue };

                handleDocFieldChange(docUpdates, newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                data.api.setEditCellValue({
                  id: data.id,
                  field: 'doc_joker_int1',
                  value: newValue,
                });
              }}
              value={findedopts}
              dataset={autoCompleteOptions.technologists}
            />
          );
        },
      },
      {
        field: 'doc_joker_int1',
        headerName: 'Technológus NORMA',
        type: 'singleSelect',
        sortable: false,
        valueGetter: (_data, row) => {
          return row.doc?.joker_int1;
        },
        editable: true,
        valueOptions: autoCompleteOptions.technologists,
        renderEditCell: (data) => {
          const findedopts =
            autoCompleteOptions.technologists.find((opt) => opt.value === data.row.doc?.joker_int1) ?? null;

          return (
            <AutoCompleteSelectUI
              name="technologistNorm"
              variant="standard"
              onChange={(_e, newVal) => {
                const newValue = newVal?.value ?? null;
                handleDocFieldChange('joker_int1', newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
              value={findedopts}
              dataset={autoCompleteOptions.technologists}
            />
          );
        },
      },
      {
        field: 'doc_joker_date2',
        headerName: 'Technológus határidő',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.doc?.joker_date2 ? new Date(row.doc?.joker_date2) : null;
        },
        renderCell: (data) => {
          return data.row.doc?.joker_date2 ? dayjs(data.row.doc?.joker_date2).format('YYYY.MM.DD') : '';
        },
        renderEditCell: (data) => {
          return (
            <DatePickerUI
              name="doc_joker_date2"
              type="date"
              variant="standard"
              fullWidth
              value={dayjs(data.row.doc?.joker_date2)}
              onChange={async (e) => {
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                handleDocFieldChange('joker_date2', newValue, data.row);
                await apiRef.current.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
            />
          );
        },
      },
      {
        field: 'joker_char7',
        headerName: 'Technológus státusz',
        type: 'singleSelect',
        editable: true,
        valueOptions: TechnologistStatuses,
        renderCell: (data) => {
          const findedopts = TechnologistStatuses.find((opt) => opt.value === data.row.joker_char7) ?? null;
          return findedopts ? findedopts.label : '';
        },
        renderEditCell: (data) => {
          return (
            <SelectUI
              option={TechnologistStatuses.filter((status) => status.value !== 'NONE')}
              value={data.row.joker_char7}
              fullWidth
              onChange={(e) => {
                const newValue = e.target.value ?? null;
                setShowConfirmDialog(true);
                setUpdData({ field: data.field, row: data.row, value: newValue });

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
              onClear={() => {
                const newValue = null;
                setShowConfirmDialog(true);
                setUpdData({ field: data.field, row: data.row, value: newValue });

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
            />
          );
        },
      },
      {
        field: 'joker_char8',
        headerName: 'Technológus megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_char8"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.joker_char8}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                // Update UI immediately
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                // Debounce the server update (per row+field key)
                scheduleDebounced(
                  `joker_char8-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },
      {
        field: 'joker_char9',
        headerName: 'Szállítói rendelés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_char9"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.joker_char9}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                scheduleDebounced(
                  `joker_char9-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row, false);
                  },
                  600
                );
              }}
            />
          );
        },
      },
      {
        field: 'joker_num1',
        headerName: 'Kooperáció költség Ft',
        type: 'number',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="joker_num1"
              type="number"
              variant="standard"
              fullWidth
              value={data.row.joker_num1}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                scheduleDebounced(
                  `joker_num1-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row, false);
                  },
                  600
                );
              }}
            />
          );
        },
      },
    ];

    const filteredColumns = allColumns
      .filter((col) => {
        const perms = ppCols?.[col.field];
        if (!perms) return false;
        return perms.read.includes(roleId) || perms.edit?.includes(roleId);
      })
      .map((col) => {
        const perms = ppCols?.[col.field];
        const editable = perms?.edit?.includes(roleId);
        return { ...col, editable };
      });
    return dataGridPremiumColumns(filteredColumns.length > 0 ? filteredColumns : allColumns);
  }, [autoCompleteOptions, params, user.roleId, reports]);

  const getRowClassName = (params) => {
    let classNames = '';

    if (lockedRowIds.includes(params.id)) classNames += 'datagrid-line-locked ';

    if (params.row.qntStatus === 'lecsomagolva') classNames += 'datagrid-line-bg_green ';
    else if (params.row.qntStatus === 'festés, csomagolás') classNames += 'datagrid-line-bg_yellow ';
    else if (params.row.qntStatus === 'Élő') classNames += 'datagrid-line-bg_white ';

    return classNames.trim();
  };

  const sourceListDialogOpen = (itemCode) => {
    setSelectedItemCode(itemCode);
  };

  const productionDialogOpen = (docId, lineNum) => {
    setSelectedOrder({ selectedOrder: docId, selectedLineNum: lineNum });
  };

  const sourceListDialogClose = () => {
    setSelectedItemCode(null);
  };

  const handleRefresh = () => {
    getData();
  };

  const getUniqueEditIds = () => {
    const permissionsObj = JSON.parse(params?.VM_PRODPLANNER_COLS ?? '{}');
    return [...new Set(Object.values(permissionsObj).flatMap((entry) => entry.edit ?? []))].sort((a, b) => a - b);
  };

  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          px: 1,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Frissítés">
          <Button
            size="small"
            sx={{ ml: 'auto' }}
            startIcon={<RefreshIcon />}
            onClick={() => {
              handleRefresh();
            }}
          >
            Frissítés
          </Button>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Box sx={sxConfig.containerBox}>
        <Box sx={sxConfig.formBox}>
          <DatagridPremiumUi
            apiRef={apiRef}
            datagridIdentifier={DataGridIdentifiers.ProductionPlannerTable}
            datagridInitialState={initialState}
            height={window.innerHeight - 100}
            columns={columns}
            rows={reports.rows}
            editMode="row"
            paginationAndfilterMode="client"
            onCellDoubleClick={(_params, event) => {
              event.defaultMuiPrevented = true;
            }}
            onCellClick={(gridParams, event) => {
              const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

              // do nothing if this row is currently locked
              if (lockedRowIds.includes(gridParams.id)) {
                return addNotification({
                  content: 'A sor zárolva van, mert szerkesztés alatt áll. Próbáld később.',
                  type: NotificationType.WARNING,
                });
              }

              const uniqueEditIds = getUniqueEditIds();
              const hasEdit = uniqueEditIds.includes(user?.roleId ?? -1);

              if (!hasEdit) return;

              for (const rowId of rowsInEditMode) {
                if (gridParams.row.id !== rowId) {
                  apiRef.current.stopRowEditMode({ id: rowId });
                }
              }

              const rowMode = apiRef.current.getRowMode(gridParams.row.id);
              if (rowMode === GridRowModes.View) {
                apiRef.current.startRowEditMode({ id: gridParams.row.id });
              }
            }}
            getRowHeight={() => 'auto'}
            getRowClassName={getRowClassName}
            gridExport={true}
            slots={{ toolbar: CustomToolbar }}
          />
        </Box>
      </Box>
      <ConfirmDialogUI
        handleChangeElement={() => {
          handleFieldChange(updData.field, updData.value, updData.row, true);
          setShowConfirmDialog(false);
        }}
        show={showConfirmDialog}
        onHide={() => {
          handleFieldChange(updData.field, updData.value, updData.row);
          setShowConfirmDialog(false);
        }}
        message={'Frissíti a rendeléshez tartozó összes tételt?'}
      />

      <SourceListDialog
        showDialog={selectedItemCode}
        onClose={sourceListDialogClose}
        selectedItemCode={selectedItemCode}
        items={autocompleteData?.items || null}
      />

      <ProductionDialog
        showDialog={selectedOrder.selectedOrder}
        onClose={() => {
          setSelectedOrder({});
        }}
        selectedOrderData={selectedOrder}
        items={autocompleteData?.items || null}
      />
    </>
  );
};

export default ProductionPlannerTable;
